import type { Language } from './translations'

const MONTH_NAMES: Record<Language, string[]> = {
  ru: [
    '',
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
  ],
  kk: [
    '',
    'қаңтар', 'ақпан', 'наурыз', 'сәуір', 'мамыр', 'маусым',
    'шілде', 'тамыз', 'қыркүйек', 'қазан', 'қараша', 'желтоқсан',
  ],
  en: [
    '',
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
}

// Названия дней недели заданы явно (индекс 0 = воскресенье, как у Date.getDay()),
// а НЕ через Intl/toLocaleDateString. Причина: браузерный Intl полагается на
// ICU-данные окружения, и на киосках/старых WebView для ru/kk их может не быть —
// тогда день недели молча откатывается на английский ("Fri", "Sat"). Явные
// таблицы дают детерминированный результат на любом устройстве (тот же приём,
// что и MONTH_NAMES выше).
const WEEKDAY_NAMES_SHORT: Record<Language, string[]> = {
  ru: ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'],
  kk: ['жс', 'дс', 'сс', 'ср', 'бс', 'жм', 'сб'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
}

const WEEKDAY_NAMES_LONG: Record<Language, string[]> = {
  ru: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
  kk: ['жексенбі', 'дүйсенбі', 'сейсенбі', 'сәрсенбі', 'бейсенбі', 'жұма', 'сенбі'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
}

export function getBcpLocale(language: Language | string): string {
  if (language === 'kk') return 'kk-KZ'
  if (language === 'en') return 'en-US'
  return 'ru-RU'
}

/**
 * Возвращает локализованное название дня недели для даты без обращения к Intl.
 * @param date  дата (день недели берётся из getDay(): 0 = воскресенье)
 * @param language текущий UI-язык
 * @param format 'short' — короткое (пн/сб), 'long' — полное (понедельник)
 */
export function formatWeekday(
  date: Date,
  language: Language | string,
  format: 'short' | 'long' = 'short',
): string {
  const table = format === 'long' ? WEEKDAY_NAMES_LONG : WEEKDAY_NAMES_SHORT
  const names = table[(language as Language)] || table.ru
  return names[date.getDay()] || ''
}

/**
 * Парсит "DD/MM/YYYY" → Date (или null, если не распарсилось).
 * Используется для дат из внешнего API УМЦ.
 */
export function parseSlotDate(dateStr: string): Date | null {
  const parts = dateStr.split('/').map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
    return null
  }
  const [day, month, year] = parts
  return new Date(year, month - 1, day)
}

/**
 * Форматирует дату "DD/MM/YYYY" под текущий язык, например:
 *   ru: "5 мая"
 *   kk: "5 мамыр"
 *   en: "5 May"
 */
export function formatSlotDate(dateStr: string, language: Language | string): string {
  const parts = dateStr.split('/').map(Number)
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) {
    return dateStr
  }
  const [day, month] = parts
  const months = MONTH_NAMES[(language as Language)] || MONTH_NAMES.ru
  const monthName = months[month] || ''
  return `${day} ${monthName}`.trim()
}

/**
 * Форматирует число с разделителями групп под текущий язык.
 */
export function formatPrice(value: number, language: Language | string): string {
  try {
    return value.toLocaleString(getBcpLocale(language))
  } catch {
    return value.toLocaleString('ru-RU')
  }
}

/**
 * Возвращает дату ближайшего понедельника в формате "DD/MM/YYYY" —
 * формат, который требует внешний API УМЦ (поэтому всегда ru-RU,
 * независимо от UI-языка).
 */
export function getMondayDateString(weekOffset = 0): string {
  const date = new Date()
  const day = date.getDay()
  const diffToMonday = day === 0 ? -6 : 1 - day
  date.setDate(date.getDate() + diffToMonday + weekOffset * 7)
  return date.toLocaleDateString('ru-RU').replace(/\./g, '/')
}
